import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LenderPartnershipsProps {
    onBack: () => void;
}

interface LenderPartnership {
    lenderName: string;
    type: 'CPACE' | 'Traditional Bank' | 'Green Finance' | 'Credit Union';
    loanRange: string;
    specialties: string[];
    contactPerson: {
        name: string;
        title: string;
        email: string;
        phone: string;
    };
    partnershipTerms: {
        referralFee: string;
        portfolioMinimum: string;
        exclusivity: boolean;
        coMarketing: boolean;
    };
    status: 'Target' | 'LOI Sent' | 'Under Review' | 'Terms Agreed' | 'Active Partner';
    notes: string;
    documentsNeeded: string[];
}

const LenderPartnerships: React.FC<LenderPartnershipsProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'cpace' | 'traditional' | 'documentation'>('cpace');

    const cpaceLenders: LenderPartnership[] = [
        {
            lenderName: "Petros PACE Finance",
            type: "CPACE",
            loanRange: "$500K - $50M",
            specialties: ["Commercial PACE", "Multifamily", "Energy Efficiency", "Renewable Energy"],
            contactPerson: {
                name: "Jennifer Martinez",
                title: "VP Business Development",
                email: "j.martinez@petrospace.com",
                phone: "(203) 298-1000"
            },
            partnershipTerms: {
                referralFee: "0.5% of funded amount",
                portfolioMinimum: "$5M annually",
                exclusivity: false,
                coMarketing: true
            },
            status: "Terms Agreed",
            notes: "Partnership agreement under legal review. Strong track record in NYC market.",
            documentsNeeded: ["Partnership Agreement", "Referral Fee Schedule", "Marketing Guidelines"]
        },
        {
            lenderName: "Nuveen Green Capital",
            type: "CPACE",
            loanRange: "$1M - $100M",
            specialties: ["Large Commercial", "Mixed-Use", "ESG Financing", "Green Buildings"],
            contactPerson: {
                name: "Michael Chen",
                title: "Director of Originations",
                email: "michael.chen@nuveen.com",
                phone: "(212) 850-4000"
            },
            partnershipTerms: {
                referralFee: "0.75% of funded amount",
                portfolioMinimum: "$10M annually",
                exclusivity: true,
                coMarketing: true
            },
            status: "LOI Sent",
            notes: "Interested in exclusive partnership for $10M+ deals. Strong brand recognition.",
            documentsNeeded: ["Letter of Intent", "Exclusivity Terms", "Volume Commitments"]
        },
        {
            lenderName: "Counterpointe Energy Solutions",
            type: "CPACE",
            loanRange: "$250K - $25M",
            specialties: ["Small Commercial", "Multifamily", "Quick Closings", "Technology Integration"],
            contactPerson: {
                name: "Sarah Johnson",
                title: "Partnership Manager",
                email: "sjohnson@counterpointeenergy.com",
                phone: "(720) 279-3900"
            },
            partnershipTerms: {
                referralFee: "0.6% of funded amount",
                portfolioMinimum: "$3M annually",
                exclusivity: false,
                coMarketing: false
            },
            status: "Active Partner",
            notes: "First active CPACE partner. API integration in progress for streamlined applications.",
            documentsNeeded: ["API Documentation", "Integration Specs", "Quarterly Reporting"]
        },
        {
            lenderName: "PACE Equity",
            type: "CPACE",
            loanRange: "$500K - $75M",
            specialties: ["Office Buildings", "Retail", "Hospitality", "Deep Energy Retrofits"],
            contactPerson: {
                name: "David Rodriguez",
                title: "Regional Manager - Northeast",
                email: "drodriguez@paceequity.com",
                phone: "(512) 222-9300"
            },
            partnershipTerms: {
                referralFee: "0.55% of funded amount",
                portfolioMinimum: "$7M annually",
                exclusivity: false,
                coMarketing: true
            },
            status: "Under Review",
            notes: "Strong appetite for NYC market. Reviewing our platform capabilities and referral volume.",
            documentsNeeded: ["Platform Demo", "Historical Referral Data", "Market Analysis"]
        }
    ];

    const traditionalLenders: LenderPartnership[] = [
        {
            lenderName: "JPMorgan Chase",
            type: "Traditional Bank",
            loanRange: "$1M - $500M",
            specialties: ["Commercial Real Estate", "Construction Loans", "Green Financing", "Equipment Finance"],
            contactPerson: {
                name: "Amanda Foster",
                title: "VP Commercial Banking",
                email: "amanda.foster@chase.com",
                phone: "(212) 270-6000"
            },
            partnershipTerms: {
                referralFee: "0.25% of funded amount",
                portfolioMinimum: "$25M annually",
                exclusivity: false,
                coMarketing: false
            },
            status: "Target",
            notes: "Large commercial focus. Potential for significant volume but lower referral fees.",
            documentsNeeded: ["Corporate Overview", "Compliance Documentation", "Volume Projections"]
        },
        {
            lenderName: "Amalgamated Bank",
            type: "Traditional Bank",
            loanRange: "$250K - $50M",
            specialties: ["Sustainable Finance", "Community Development", "Energy Efficiency", "Social Impact"],
            contactPerson: {
                name: "Lisa Park",
                title: "SVP Sustainable Finance",
                email: "lpark@amalgamatedbank.com",
                phone: "(212) 895-4400"
            },
            partnershipTerms: {
                referralFee: "0.4% of funded amount",
                portfolioMinimum: "$8M annually",
                exclusivity: false,
                coMarketing: true
            },
            status: "LOI Sent",
            notes: "Mission-aligned bank with strong ESG focus. Excellent cultural fit.",
            documentsNeeded: ["Partnership LOI", "ESG Impact Metrics", "Sustainability Report"]
        }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Target': return 'bg-gray-100 text-gray-800';
            case 'LOI Sent': return 'bg-blue-100 text-blue-800';
            case 'Under Review': return 'bg-yellow-100 text-yellow-800';
            case 'Terms Agreed': return 'bg-orange-100 text-orange-800';
            case 'Active Partner': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const downloadLOITemplate = () => {
        // In a real app, this would download an actual file
        alert('LOI Template download would start here');
    };

    const downloadPartnershipAgreement = () => {
        // In a real app, this would download an actual file
        alert('Partnership Agreement template download would start here');
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Lender Partnerships</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        CPACE financing and traditional lending partnerships with comprehensive documentation
                    </p>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-green-800 text-sm">
                            <strong>💰 Revenue Model:</strong> Generate referral fees of 0.25-0.75% on funded loan amounts. 
                            Target $50M+ in annual loan volume for $250K-$375K in referral revenue.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('cpace')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'cpace'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🏛️ CPACE Lenders
                            </button>
                            <button
                                onClick={() => setActiveTab('traditional')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'traditional'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🏦 Traditional Lenders
                            </button>
                            <button
                                onClick={() => setActiveTab('documentation')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'documentation'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📄 Documentation & LOI
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* CPACE Lenders Tab */}
                        {activeTab === 'cpace' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">CPACE Financing Partners</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Total Lending Capacity</div>
                                        <div className="text-2xl font-bold text-green-600">$250M+</div>
                                        <div className="text-xs text-gray-500 mt-1">Combined Annual Volume</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {cpaceLenders.map((lender, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{lender.lenderName}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lender.status)}`}>
                                                            {lender.status}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Loan Range:</span>
                                                            <p className="text-gray-900 font-semibold">{lender.loanRange}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Referral Fee:</span>
                                                            <p className="text-green-600 font-semibold">{lender.partnershipTerms.referralFee}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Portfolio Min:</span>
                                                            <p className="text-gray-900 font-semibold">{lender.partnershipTerms.portfolioMinimum}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Key Contact</h4>
                                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                        <p className="font-medium text-gray-900">{lender.contactPerson.name}</p>
                                                        <p className="text-sm text-gray-600">{lender.contactPerson.title}</p>
                                                        <p className="text-sm text-blue-600 mt-1">{lender.contactPerson.email}</p>
                                                        <p className="text-sm text-gray-600">{lender.contactPerson.phone}</p>
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Specialties</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {lender.specialties.map((specialty, idx) => (
                                                            <span key={idx} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                                                                {specialty}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Partnership Terms</h4>
                                                    <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-2">
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-600">Exclusivity:</span>
                                                            <span className={`text-sm font-medium ${lender.partnershipTerms.exclusivity ? 'text-orange-600' : 'text-green-600'}`}>
                                                                {lender.partnershipTerms.exclusivity ? 'Exclusive' : 'Non-Exclusive'}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-600">Co-Marketing:</span>
                                                            <span className={`text-sm font-medium ${lender.partnershipTerms.coMarketing ? 'text-green-600' : 'text-gray-600'}`}>
                                                                {lender.partnershipTerms.coMarketing ? 'Yes' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Documents Needed</h4>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        {lender.documentsNeeded.map((doc, idx) => (
                                                            <li key={idx} className="flex items-start">
                                                                <span className="text-orange-500 mr-2">•</span>
                                                                {doc}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <h4 className="font-semibold text-yellow-800 mb-1">Notes</h4>
                                                <p className="text-yellow-700 text-sm">{lender.notes}</p>
                                            </div>

                                            <div className="flex justify-end space-x-3">
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    View Terms
                                                </button>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    Contact
                                                </button>
                                                <button className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    Send LOI
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Traditional Lenders Tab */}
                        {activeTab === 'traditional' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">Traditional Lending Partners</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Total Lending Capacity</div>
                                        <div className="text-2xl font-bold text-blue-600">$550M+</div>
                                        <div className="text-xs text-gray-500 mt-1">Combined Annual Volume</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {traditionalLenders.map((lender, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{lender.lenderName}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(lender.status)}`}>
                                                            {lender.status}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Loan Range:</span>
                                                            <p className="text-gray-900 font-semibold">{lender.loanRange}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Referral Fee:</span>
                                                            <p className="text-green-600 font-semibold">{lender.partnershipTerms.referralFee}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Portfolio Min:</span>
                                                            <p className="text-gray-900 font-semibold">{lender.partnershipTerms.portfolioMinimum}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Key Contact</h4>
                                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                        <p className="font-medium text-gray-900">{lender.contactPerson.name}</p>
                                                        <p className="text-sm text-gray-600">{lender.contactPerson.title}</p>
                                                        <p className="text-sm text-blue-600 mt-1">{lender.contactPerson.email}</p>
                                                        <p className="text-sm text-gray-600">{lender.contactPerson.phone}</p>
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Specialties</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {lender.specialties.map((specialty, idx) => (
                                                            <span key={idx} className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                                                                {specialty}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <h4 className="font-semibold text-yellow-800 mb-1">Notes</h4>
                                                <p className="text-yellow-700 text-sm">{lender.notes}</p>
                                            </div>

                                            <div className="flex justify-end space-x-3">
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    View Terms
                                                </button>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    Contact
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Documentation & LOI Tab */}
                        {activeTab === 'documentation' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Partnership Documentation & Letters of Intent</h2>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Legal Documents</h3>
                                        <div className="space-y-4">
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Partnership Agreement Template</h4>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Comprehensive partnership agreement template covering referral fees, 
                                                    exclusivity terms, marketing guidelines, and performance metrics.
                                                </p>
                                                <div className="flex space-x-3">
                                                    <button 
                                                        onClick={downloadPartnershipAgreement}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                    >
                                                        Download Template
                                                    </button>
                                                    <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                        View Sample
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Letter of Intent (LOI) Template</h4>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Professional LOI template for initial partnership discussions. 
                                                    Includes terms outline, volume commitments, and timeline.
                                                </p>
                                                <div className="flex space-x-3">
                                                    <button 
                                                        onClick={downloadLOITemplate}
                                                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors"
                                                    >
                                                        Download LOI
                                                    </button>
                                                    <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                        View Sample
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Referral Fee Schedule</h4>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Standardized fee structure document with tiered rates based on 
                                                    loan volume and partnership level.
                                                </p>
                                                <div className="flex space-x-3">
                                                    <button className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                        Download Schedule
                                                    </button>
                                                    <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                        View Rates
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Process & Guidelines</h3>
                                        <div className="space-y-4">
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Partnership Onboarding Checklist</h4>
                                                <ul className="text-sm text-gray-600 space-y-2">
                                                    <li className="flex items-start">
                                                        <span className="text-green-500 mr-2">✓</span>
                                                        Initial contact and needs assessment
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-green-500 mr-2">✓</span>
                                                        Platform demonstration and Q&A
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-blue-500 mr-2">→</span>
                                                        LOI negotiation and execution
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-gray-400 mr-2">○</span>
                                                        Due diligence and compliance review
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-gray-400 mr-2">○</span>
                                                        Partnership agreement finalization
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-gray-400 mr-2">○</span>
                                                        Technical integration and testing
                                                    </li>
                                                    <li className="flex items-start">
                                                        <span className="text-gray-400 mr-2">○</span>
                                                        Launch and ongoing support
                                                    </li>
                                                </ul>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Due Diligence Requirements</h4>
                                                <div className="text-sm text-gray-600 space-y-2">
                                                    <div><strong>Legal:</strong> Corporate structure, licenses, compliance history</div>
                                                    <div><strong>Financial:</strong> Financial statements, lending capacity, credit ratings</div>
                                                    <div><strong>Operational:</strong> Loan processing capabilities, technology systems</div>
                                                    <div><strong>References:</strong> Existing partnerships, customer testimonials</div>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-6 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-3">Performance Metrics</h4>
                                                <div className="text-sm text-gray-600 space-y-1">
                                                    <div>• Loan application approval rate</div>
                                                    <div>• Average time to funding</div>
                                                    <div>• Customer satisfaction scores</div>
                                                    <div>• Referral conversion rates</div>
                                                    <div>• Volume commitments tracking</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Pipeline Status</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Active Partnerships</h4>
                                            <div className="text-3xl font-bold text-green-600">3</div>
                                            <p className="text-sm text-gray-600">Generating referrals</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">In Negotiation</h4>
                                            <div className="text-3xl font-bold text-orange-600">2</div>
                                            <p className="text-sm text-gray-600">Terms being finalized</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">LOI Sent</h4>
                                            <div className="text-3xl font-bold text-blue-600">4</div>
                                            <p className="text-sm text-gray-600">Awaiting response</p>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Target Pipeline</h4>
                                            <div className="text-3xl font-bold text-gray-600">8</div>
                                            <p className="text-sm text-gray-600">Qualified prospects</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to="/real-estate-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🏢</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real Estate Partners</h3>
                            <p className="text-sm text-gray-600">Property management and development partnerships</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/contractor-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🔧</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contractor Network</h3>
                            <p className="text-sm text-gray-600">Vetted contractors and service providers</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/staff-hiring"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">👥</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Staff Hiring</h3>
                            <p className="text-sm text-gray-600">Remaining team member recruitment</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default LenderPartnerships;
